/**
 * Web-side audio capability snapshot for Stats for Nerds — ported from
 * green hornet's getDeviceAudioInfo.web. The Web Audio API exposes the
 * sample rate of the AudioContext (which mirrors the OS-level mix
 * rate); bit depth isn't directly available on the web platform so we
 * infer it from the sample rate (24-bit at ≥48 kHz, 16-bit otherwise).
 */
export interface DeviceAudioInfo {
    readonly deviceName: string
    readonly sampleRate: number
    readonly bitDepth: number | null
}

let audioContextInstance: AudioContext | null = null

function getAudioContext(): AudioContext {
    if (!audioContextInstance) {
        audioContextInstance = new AudioContext()
    }
    return audioContextInstance
}

function getBrowserName(): string {
    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes('edg/')) return 'Edge'
    if (ua.includes('chrome') && !ua.includes('edg')) return 'Chrome'
    if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari'
    if (ua.includes('firefox')) return 'Firefox'
    if (ua.includes('opera') || ua.includes('opr/')) return 'Opera'
    return 'Browser'
}

function inferBitDepth(sampleRate: number): number {
    if (sampleRate >= 48000) return 24
    return 16
}

export function getDeviceAudioInfo(): DeviceAudioInfo {
    try {
        const audioContext = getAudioContext()
        const sampleRate = audioContext.sampleRate
        return {
            deviceName: `${getBrowserName()} Browser`,
            sampleRate,
            bitDepth: inferBitDepth(sampleRate),
        }
    } catch {
        return { deviceName: 'Browser', sampleRate: 48000, bitDepth: 16 }
    }
}

/**
 * Best-effort output device probe. `enumerateDevices()` only returns
 * labels once the user has granted media permission; without it we
 * report a generic "Audio Output". This matches hornet's behaviour.
 */
export async function getOutputAudioInfo(): Promise<DeviceAudioInfo> {
    try {
        const audioContext = getAudioContext()
        const sampleRate = audioContext.sampleRate
        let outputName = 'Audio Output'
        if (typeof navigator.mediaDevices.enumerateDevices === 'function') {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                const audioOutputs = devices.filter((d) => d.kind === 'audiooutput')
                if (audioOutputs.length > 0 && audioOutputs[0].label) {
                    outputName = audioOutputs[0].label
                }
            } catch {
                // permissions blocked or API absent — keep default
            }
        }
        return { deviceName: outputName, sampleRate, bitDepth: inferBitDepth(sampleRate) }
    } catch {
        return { deviceName: 'Audio Output', sampleRate: 48000, bitDepth: 16 }
    }
}
